import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { FooterComponent } from '../../layout/footer/footer.component';
import { HeaderComponent } from '../../layout/header/header.component';
import { FooterNavComponent } from '../../shared/components/footer-nav/footer-nav.component';
import {
  VirtualKeyboardComponent,
  VirtualKeyboardKey,
} from '../../shared/components/virtual-keyboard/virtual-keyboard.component';
import { Router } from '@angular/router';
import { VisitService } from '../../core/visit/visit.service';
import { NotificationService } from '../../core/notifications/notification.service';
import { WwsIndividu, WwsService } from '../../core/services/wws.service';
import { wwsIndividuToNotifiedContact } from '../../core/visit/wws-host-contact';

interface PersonContact {
  id: string;
  displayName: string;
}

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;

@Component({
  selector: 'app-person-notification',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    FooterNavComponent,
    VirtualKeyboardComponent,
  ],
  templateUrl: './person-notification.component.html',
  styleUrl: './person-notification.component.scss',
})
export class PersonNotificationComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly visitService = inject(VisitService);
  private readonly wwsService = inject(WwsService);
  private readonly notifications = inject(NotificationService);

  readonly searchQuery = signal('');
  readonly selectedPersonId = signal<string | null>(null);

  private readonly hosts = signal<WwsIndividu[]>([]);

  readonly filteredContacts = computed<PersonContact[]>(() =>
    this.hosts().map((host, index) => ({
      id: this.hostId(host, index),
      displayName: this.getDisplayName(host),
    })),
  );

  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  onKeyPress(key: VirtualKeyboardKey): void {
    this.searchQuery.update((value) => value + key);
    this.scheduleSearch();
  }

  onBackspace(): void {
    this.searchQuery.update((value) => value.slice(0, -1));
    this.scheduleSearch();
  }

  onSpace(): void {
    this.searchQuery.update((value) => value + ' ');
    this.scheduleSearch();
  }

  selectPerson(id: string): void {
    const contact = this.filteredContacts().find((c) => c.id === id);
    const hostIndividu = this.findHostById(id);
    const hostContact = wwsIndividuToNotifiedContact(hostIndividu);

    this.selectedPersonId.set(id);
    this.visitService.updateFlow({
      notifiedPersonId: id,
      notifiedPersonName: contact?.displayName,
      notifiedPersonHost: hostContact,
    });
    this.visitService.trackEvent('person_selected', { personId: id });

    if (hostContact) {
      const visitor = this.visitService.current()?.visitor ?? {};
      this.notifications.notifyHost(visitor, hostContact);
    }

    void this.router.navigate(['/visitor-welcome']);
  }

  private findHostById(id: string): WwsIndividu | undefined {
    return this.hosts().find((h, idx) => this.hostId(h, idx) === id);
  }

  ngOnDestroy(): void {
    if (this.searchTimeout != null) {
      clearTimeout(this.searchTimeout);
    }
  }

  /** Triggers a WWS search after MIN_SEARCH_LENGTH chars with debounce. */
  private scheduleSearch(): void {
    if (this.searchTimeout != null) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    const q = this.searchQuery().trim();
    if (q.length >= MIN_SEARCH_LENGTH) {
      this.searchTimeout = setTimeout(() => this.runSearch(q), SEARCH_DEBOUNCE_MS);
    } else {
      this.hosts.set([]);
    }
  }

  private runSearch(q: string): void {
    this.hosts.set([]);
    this.wwsService.searchUserPosition({ q }).subscribe({
      next: (result) => this.hosts.set(result),
      error: () => this.hosts.set([]),
    });
  }

  private getDisplayName(host: WwsIndividu): string {
    if (host.name) return String(host.name);
    if (host.displayName) return String(host.displayName);
    const first = host.firstName ?? (host as Record<string, unknown>)['first_name'] ?? '';
    const last = host.lastName ?? (host as Record<string, unknown>)['last_name'] ?? '';
    return [first, last].filter(Boolean).join(' ').trim();
  }

  private hostId(host: WwsIndividu, fallbackIndex: number): string {
    return host.id != null ? String(host.id) : `idx-${fallbackIndex}`;
  }
}
